import {
  AddOnOverrideState,
  AddOnBillingStatus,
  AddOnOverrideResolution,
  AddOnKey,
  type IOrganisation,
  type IOrganisationSubscription,
  type IPractice,
  type ISubscriptionAddOn,
} from '@principle-theorem/principle-core/interfaces';
import {
  initMoment,
  toMoment,
  type DocumentReference,
} from '@principle-theorem/shared';
import { Moment } from 'moment-timezone';
import { Organisation } from '../models/organisation/organisation';

/**
 * Bundle membership for each add-on key. When an add-on key is enabled, every
 * key in its bundle is also added to the enabled set by `expandWithBundles`.
 * Non-transitive: bundle members are NOT themselves expanded.
 *
 * Entry point for upcoming gated features; intentionally unused until then.
 * MVP: every product maps to `[]` — no bundles wired up yet. Future bundle
 * products (e.g. an "AI Imaging Suite" SKU that grants several sub-products)
 * are added here without changing any consumer logic.
 */
export const ADD_ON_BUNDLE_MAP: Record<AddOnKey, AddOnKey[]> = {
  [AddOnKey.AiImagingAnalysis]: [],
  [AddOnKey.AiReceptionist]: [],
  [AddOnKey.AiScribe]: [],
};

export class SubscriptionAddOns {
  /**
   * Entry point for upcoming gated features; intentionally unused until then.
   * AddOns are delineated by SUBSCRIPTION, not by organisation: a product
   * bought on one subscription covers every practice on that subscription, and
   * no practice on a different one. Resolution therefore selects the single
   * subscription covering `practiceRef` via `Organisation.getSubscription`,
   * which is the canonical rule already used elsewhere (undeleted only,
   * practice-specific match first, falling back to the org-wide subscription).
   * Resolving org-wide instead would over-grant across sibling practices.
   */
  static async forPractice(
    organisationRef: DocumentReference<IOrganisation>,
    practiceRef: DocumentReference<IPractice> | undefined,
    now: Moment = initMoment()
  ): Promise<Set<AddOnKey>> {
    const subscription = await Organisation.getSubscription(
      organisationRef,
      practiceRef
    );
    return SubscriptionAddOns.enabledKeys(subscription, now);
  }

  /** Resolves billing, live overrides and bundle membership for one subscription. */
  static enabledKeys(
    subscription: IOrganisationSubscription | undefined,
    now: Moment = initMoment()
  ): Set<AddOnKey> {
    const enabledKeys = new Set<AddOnKey>();

    for (const addOn of subscription?.addOns ?? []) {
      const overrideResolution = SubscriptionAddOns.resolveOverride(addOn, now);
      const enabled = SubscriptionAddOns.applyOverride(
        addOn.active,
        overrideResolution
      );

      if (enabled) {
        enabledKeys.add(addOn.addOnKey);
      }
    }

    return SubscriptionAddOns.expandWithBundles(enabledKeys);
  }

  /**
   * Entry point for upcoming gated features; intentionally unused until then.
   * Expands a set of enabled add-on keys to include `ADD_ON_BUNDLE_MAP` members.
   * Non-transitive — bundle members are not themselves expanded — so the
   * graph stays one level deep and consumers can rely on a single pass.
   *
   * @param productKeys input set is not mutated; a new Set is returned.
   */
  static expandWithBundles(productKeys: Set<AddOnKey>): Set<AddOnKey> {
    const result = new Set<AddOnKey>(productKeys);

    for (const key of productKeys) {
      const bundleMembers = ADD_ON_BUNDLE_MAP[key];

      if (bundleMembers) {
        for (const member of bundleMembers) {
          result.add(member);
        }
      }
    }

    return result;
  }

  /**
   * Canonical "is this add-on currently covered by billing?" predicate.
   * `PastDue` is treated as active — it is Stripe's grace period where the
   * user has not paid the latest invoice but the subscription is still in
   * good standing. `Unpaid` and `Canceled` are not active.
   *
   * The override layer is NOT applied here; use `resolveOverride` +
   * `applyOverride` for the user-facing answer.
   */
  static isAddOnActive(addOn: ISubscriptionAddOn): boolean {
    return (
      addOn.billingStatus === AddOnBillingStatus.Active ||
      addOn.billingStatus === AddOnBillingStatus.PastDue
    );
  }

  /**
   * Reduces a persisted `IAddOnOverride` (FORCE-only union) to a
   * runtime tristate `AddOnOverrideResolution`.
   *
   * Returns `Auto` when:
   *   - no override is set, OR
   *   - the override has an `expiresAt` that is now in the past (handled by
   *     this function in real-time AND materialised by the hourly cron
   *     `recomputeAddOnActiveOnExpiry`, which also emits an audit event).
   *
   * Otherwise mirrors the persisted `state`.
   */
  static resolveOverride(
    addOn: ISubscriptionAddOn,
    now: Moment
  ): AddOnOverrideResolution {
    const override = addOn.override;

    if (!override) {
      return AddOnOverrideResolution.Auto;
    }

    if (override.expiresAt) {
      const expiresAt = toMoment(override.expiresAt);

      if (expiresAt.isSameOrBefore(now)) {
        return AddOnOverrideResolution.Auto;
      }
    }

    return override.state === AddOnOverrideState.ForceEnabled
      ? AddOnOverrideResolution.ForceEnabled
      : AddOnOverrideResolution.ForceDisabled;
  }

  /**
   * Combines the Stripe-derived boolean with a resolved tristate. `Auto`
   * passes through the Stripe-derived value; `ForceEnabled`/`ForceDisabled`
   * unconditionally override it.
   */
  static applyOverride(
    addOnActive: boolean,
    override: AddOnOverrideResolution
  ): boolean {
    if (override === AddOnOverrideResolution.ForceEnabled) {
      return true;
    }

    if (override === AddOnOverrideResolution.ForceDisabled) {
      return false;
    }

    return addOnActive;
  }
}
